/**
 * LibraryShareDialog — Access & Sharing dialog for libraries.
 * Mirrors the Board ShareModal pattern: visibility selector + member search + member list.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { VisibilitySelector, VisibilityMode } from '../../components/VisibilitySelector';
import { LibraryMemberSearch } from './LibraryMemberSearch';
import * as pagesApi from '../../api/pages-api';
import type { LibraryVisibility, LibraryMemberRole, LibraryMember } from '../../api/pages-api';
import { usePagesContext } from '../../state/pages-context';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface LibraryShareDialogProps {
  readonly libraryId: string;
  readonly libraryName: string;
  readonly currentVisibility: LibraryVisibility;
  /** Trigger element — if not provided, renders a default button */
  readonly trigger?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LibraryShareDialog({
  libraryId,
  libraryName,
  currentVisibility,
  trigger,
}: LibraryShareDialogProps): React.ReactElement {
  const { fetchLibraries } = usePagesContext();
  const [open, setOpen] = useState(false);
  const [visibility, setVisibility] = useState<VisibilityMode>(
    currentVisibility === 'public-edit' ? 'public-edit'
      : currentVisibility === 'public-read' || currentVisibility === 'public' as string ? 'public-read'
      : 'private'
  );
  const [members, setMembers] = useState<LibraryMember[]>([]);
  const [userNames, setUserNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);

  // Sync visibility from props
  useEffect(() => {
    setVisibility(
      currentVisibility === 'public-edit' ? 'public-edit'
        : currentVisibility === 'public-read' || currentVisibility === 'public' as string ? 'public-read'
        : 'private'
    );
  }, [currentVisibility]);

  // Fetch members when dialog opens
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      pagesApi.listMembers(libraryId),
      pagesApi.listUsers(),
    ]).then(([membersRes, usersRes]) => {
      const list = membersRes.data ?? (membersRes as unknown as { members: LibraryMember[] }).members ?? [];
      setMembers(list);
      const userList: pagesApi.UserInfo[] = usersRes.data ?? [];
      const nameMap = new Map<string, string>();
      for (const u of userList) {
        nameMap.set(u.userId, u.displayName);
      }
      setUserNames(nameMap);
    }).catch(() => {
      setMembers([]);
    }).finally(() => {
      setLoading(false);
    });
  }, [open, libraryId]);

  const handleVisibilityChange = useCallback(async (newVisibility: VisibilityMode) => {
    await pagesApi.updateLibrary(libraryId, { visibility: newVisibility as LibraryVisibility });
    setVisibility(newVisibility);
    fetchLibraries();
  }, [libraryId, fetchLibraries]);

  const handleRoleChange = useCallback(async (userId: string, role: LibraryMemberRole) => {
    await pagesApi.updateMemberRole(libraryId, userId, { role });
    setMembers((prev) => prev.map((m) => m.userId === userId ? { ...m, role } : m));
  }, [libraryId]);

  const handleRemoveMember = useCallback(async (userId: string) => {
    await pagesApi.removeMember(libraryId, userId);
    setMembers((prev) => prev.filter((m) => m.userId !== userId));
  }, [libraryId]);

  const resolveUserName = (userId: string): string => userNames.get(userId) ?? userId;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm" className="inline-flex items-center gap-1.5">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
              <path d="M7 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM14.5 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM1.615 16.428a1.224 1.224 0 0 1-.569-1.175 6.002 6.002 0 0 1 11.908 0c.058.467-.172.92-.57 1.174A9.953 9.953 0 0 1 7 18a9.953 9.953 0 0 1-5.385-1.572ZM14.5 16h-.106c.07-.297.088-.611.048-.933a7.47 7.47 0 0 0-1.588-3.755 4.502 4.502 0 0 1 5.874 2.636.818.818 0 0 1-.36.98A7.465 7.465 0 0 1 14.5 16Z" />
            </svg>
            Access &amp; Sharing
          </Button>
        )}
      </DialogTrigger>

      {open && (
        <DialogContent className="max-w-[480px]" aria-labelledby="library-share-title">
          <DialogHeader>
            <DialogTitle id="library-share-title">Access &amp; Sharing — {libraryName}</DialogTitle>
          </DialogHeader>

          {/* Visibility selector */}
          <div className="mb-4 pb-4 border-b border-border">
            <VisibilitySelector
              currentVisibility={visibility}
              onVisibilityChange={handleVisibilityChange}
            />
          </div>

          {/* Add member search */}
          <div className="mb-4">
            <h4 className="text-xs font-medium text-muted-foreground uppercase mb-2">Add members</h4>
            <LibraryMemberSearch
              libraryId={libraryId}
              existingMemberIds={members.map((m) => m.userId)}
              defaultRole="editor"
              onMemberAdded={(member) => setMembers((prev) => [...prev, member])}
            />
          </div>

          {/* Member list */}
          <div>
            <h4 className="text-xs font-medium text-muted-foreground uppercase mb-2">
              Members ({members.length})
            </h4>

            {loading && <p className="text-xs text-muted-foreground">Loading...</p>}

            {!loading && members.length === 0 && (
              <p className="text-xs text-muted-foreground">No members yet.</p>
            )}

            {!loading && members.length > 0 && (
              <ul className="space-y-2 max-h-[240px] overflow-y-auto" role="list">
                {members.map((member) => (
                  <li key={member.userId} className="flex items-center justify-between gap-2">
                    <span className="text-sm truncate flex-1 min-w-0">
                      {resolveUserName(member.userId)}
                    </span>
                    <select
                      value={member.role}
                      onChange={(e) => handleRoleChange(member.userId, e.target.value as LibraryMemberRole)}
                      className="text-xs border rounded px-2 py-1 bg-background"
                      aria-label={`Role for ${resolveUserName(member.userId)}`}
                    >
                      <option value="admin">Admin</option>
                      <option value="editor">Editor</option>
                      <option value="viewer">Viewer</option>
                    </select>
                    <button
                      onClick={() => handleRemoveMember(member.userId)}
                      className="text-destructive hover:text-destructive/80 px-1 text-sm"
                      aria-label={`Remove ${resolveUserName(member.userId)}`}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
}
